/**
 * Beu admin — one conversation / call / send, line by line: who said what
 * and when, tool calls, and per line the model, tokens, latency, estimated
 * and reconciled cost and provider ids (CallSid, MessageSid, request ids).
 *
 * Opening this READS CONVERSATION CONTENT. The backend logs every read
 * (adminaccesslogs: who, what, when, reason), so the page asks for a reason
 * first. Transcripts removed by retention show as "expired"; costs remain.
 */
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AdminWrapper from '../AdminWrapper';
import { adminAiService } from '../../../_services/adminAi.service';
import { KIND_LABELS, fmtPence, fmtMs, fmtTime, fmtTokens, lineCost, isReconciled } from './aiFormat';
import { FlagPills } from './AdminAiOverview';
import '../Admin.css';
import '../financials/AdminFinancials.css';
import './AdminAi.css';

export function LedgerDetail({ ledger }) {
  if (!ledger) return null;
  const ids = ledger.providerIds || {};
  return (
    <div className="ai-ov-ledger">
      <div className="ai-ov-ledger-row">
        <span className="ai-ov-tag">{KIND_LABELS[ledger.kind] || ledger.kind}</span>
        {ledger.model && <span className="fin-mono">{ledger.model}</span>}
        <span>{fmtTokens(ledger.tokens)}</span>
        {ledger.latencyMs?.model != null && <span>model {fmtMs(ledger.latencyMs.model)}</span>}
        {ledger.durationMs != null && <span>took {fmtMs(ledger.durationMs)}</span>}
        <span>est {fmtPence(ledger.estCostPence)}</span>
        {isReconciled(ledger) && <span className="ai-ov-pos">actual {fmtPence(ledger.actualCostPence)} ({fmtTime(ledger.actualAt)})</span>}
        {ledger.status === 'error' && <span className="ai-ov-flag bad">failed</span>}
        <FlagPills flags={(ledger.flags || []).filter((f) => f !== 'fx_unverified')} />
      </div>
      {(ledger.calls || []).length > 0 && (
        <table className="ai-ov-calls">
          <tbody>
            {ledger.calls.map((c, i) => c.type === 'tool' ? (
              <tr key={i}><td>round {c.round}</td><td>tool <code>{c.name}</code></td><td>{fmtMs(c.ms)}</td><td>{c.ok ? 'ok' : `refused: ${c.refusedReason || 'error'}`}</td><td /></tr>
            ) : (
              <tr key={i}><td>round {c.round}</td><td>model <code>{c.model}</code> {c.stopReason ? `(${c.stopReason})` : ''}</td><td>{fmtMs(c.latencyMs)}</td><td>{fmtTokens(c.tokens)}</td><td className="fin-mono">{c.requestId || ''}{c.estCostPence != null ? ` · ${fmtPence(c.estCostPence)}` : ''}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      {(ids.callSid || ids.messageSid || ids.requestId) && (
        <div className="ai-ov-ids fin-mono">
          {ids.callSid && <span>CallSid {ids.callSid}</span>}
          {ids.messageSid && <span>MessageSid {ids.messageSid}</span>}
          {ids.requestId && <span>Request {ids.requestId}</span>}
        </div>
      )}
      {Array.isArray(ledger.costBreakdown) && ledger.costBreakdown.length > 0 && ledger.costBreakdown[0].what && (
        <div className="ai-ov-ids fin-dim">
          {ledger.costBreakdown.map((b, i) => <span key={i}>{b.what}: {b.qty} × {b.unit} {b.currency} = {fmtPence(b.pence)}</span>)}
        </div>
      )}
      <div className="ai-ov-ids fin-dim"><span>rate card {ledger.rateCardVersion}</span>{ledger.backfilled && <span>backfilled</span>}</div>
    </div>
  );
}

class AdminAiSession extends Component {
  state = { reason: '', started: false, loading: false, error: null, data: null };

  params() { return this.props.match?.params || {}; }

  open = async (e) => {
    if (e) e.preventDefault();
    this.setState({ started: true, loading: true, error: null });
    try {
      const { kind, id } = this.params();
      const data = await adminAiService.getSession(kind, id, this.state.reason);
      this.setState({ data, loading: false });
    } catch (err) {
      this.setState({ error: err.message || String(err), loading: false });
    }
  };

  renderGate() {
    return (
      <form className="fin-section ai-ov-gate" onSubmit={this.open}>
        <h3 className="fin-section-title">This shows the conversation itself</h3>
        <p className="fin-dim">Opening it is logged against your account with the time and your reason. Only open conversations you need to.</p>
        <input className="ai-ov-search" placeholder="Reason (e.g. cost spike check, salon dispute)" value={this.state.reason} onChange={(e) => this.setState({ reason: e.target.value })} />
        <button className="ai-ov-btn primary" type="submit">Open conversation</button>
      </form>
    );
  }

  renderMeta(d) {
    const m = d.meta || {};
    const t = d.totals || {};
    const items = [
      ['Kind', m.kind], ['Channel', m.channel], ['Salon', m.salonId ? <Link to={`/beu-admin-x7k9/ai/salon/${m.salonId}`}>{String(m.salonId)}</Link> : '—'],
      ['First line', fmtTime(t.firstAt)], ['Last line', fmtTime(t.lastAt)],
      ['Billable events', t.events], ['Tokens', fmtTokens(t.tokens)], ['Total cost', fmtPence(t.costPence)]
    ];
    if (m.call) {
      items.push(['Call', <span className="fin-mono">{m.call.callSid}</span>]);
      items.push(['Call start → end', `${fmtTime(m.call.startedAt)} → ${fmtTime(m.call.endedAt, false)}`]);
      items.push(['Call length', `${fmtMs(m.call.durationMs)} (${m.call.minutes} billed min)`]);
    }
    if (m.title) items.push(['Title', m.title]);
    if (m.model) items.push(['Model', m.model]);
    return (
      <div className="fin-section">
        <div className="ai-ov-meta">
          {items.map(([k, v]) => <div key={k}><div className="fin-kpi-tile-label">{k}</div><div>{v == null || v === '' ? '—' : v}</div></div>)}
        </div>
        {d.contentStatus === 'expired' && <p className="ai-ov-expired">The transcript has been removed by its retention period. The costs below are kept.</p>}
      </div>
    );
  }

  renderLines(d) {
    let prev = null;
    return (
      <div className="fin-section">
        <div className="fin-section-header"><h3 className="fin-section-title">Every line ({d.lines.length})</h3></div>
        <ol className="ai-ov-timeline">
          {d.lines.map((l, i) => {
            const gap = prev && l.at ? new Date(l.at) - new Date(prev) : null;
            prev = l.at || prev;
            const cost = lineCost(l.ledger);
            return (
              <li key={i} className={`ai-ov-line role-${l.role}`}>
                <div className="ai-ov-line-head">
                  <span className="fin-mono">{fmtTime(l.at)}</span>
                  {gap != null && gap >= 0 && <span className="fin-dim">+{fmtMs(gap)}</span>}
                  <span className="ai-ov-speaker">{l.speaker}</span>
                  {cost != null && <span className="ai-ov-line-cost">{fmtPence(cost)}</span>}
                </div>
                {l.expired ? <div className="ai-ov-expired">expired</div> : l.text != null && <div className="ai-ov-text">{l.text}</div>}
                {l.tool && (
                  <div className="ai-ov-tool">
                    <code>{l.tool.name}</code>
                    {l.tool.refusedReason && <span className="ai-ov-flag bad">refused: {l.tool.refusedReason}</span>}
                    {l.tool.argsSummary && <div className="fin-dim">args: <span className="fin-mono">{l.tool.argsSummary}</span></div>}
                    {l.tool.resultSummary && <div className="fin-dim">result: <span className="fin-mono">{l.tool.resultSummary}</span></div>}
                  </div>
                )}
                {l.tokens && !l.ledger && <div className="fin-dim">{l.model} · {fmtTokens(l.tokens)}</div>}
                <LedgerDetail ledger={l.ledger} />
              </li>
            );
          })}
        </ol>
      </div>
    );
  }

  renderUnplaced(d) {
    // Ledger rows not tied to a single line (e.g. the call itself, a title).
    const placed = new Set(d.lines.filter((l) => l.ledger).map((l) => l.ledger.ledgerId));
    const rest = (d.ledger || []).filter((e) => !placed.has(e.ledgerId));
    if (!rest.length) return null;
    return (
      <div className="fin-section">
        <div className="fin-section-header"><h3 className="fin-section-title">Other costs in this session</h3></div>
        {rest.map((e) => <div key={e.ledgerId} className="ai-ov-line"><div className="ai-ov-line-head"><span className="fin-mono">{fmtTime(e.startedAt)}</span><span className="ai-ov-line-cost">{fmtPence(lineCost(e))}</span></div><LedgerDetail ledger={e} /></div>)}
      </div>
    );
  }

  render() {
    const { started, loading, error, data } = this.state;
    const { kind, id } = this.params();
    return (
      <AdminWrapper>
        <div className="admin-page-header ai-ov-header">
          <div>
            <Link to="/beu-admin-x7k9/ai" className="ai-ov-back">← AI overview</Link>
            <h1 className="admin-page-title">{kind === 'call' ? 'Call' : kind === 'event' ? 'Event' : 'Conversation'}</h1>
            <p className="admin-page-subtitle fin-mono">{id}</p>
          </div>
        </div>
        {!started && this.renderGate()}
        {loading && <div className="fin-loading">Loading…</div>}
        {error && <div className="fin-error">{error}</div>}
        {!loading && !error && data && (
          <div>
            {this.renderMeta(data)}
            {this.renderLines(data)}
            {this.renderUnplaced(data)}
          </div>
        )}
      </AdminWrapper>
    );
  }
}

export default AdminAiSession;
